<template>
  <div class="edit-exam-view exam-view-container">
    <div class="mb-6">
      <div class="flex items-center mb-4">
        <button
          @click="handleBack"
          class="flex items-center text-secondary-text dark:text-secondary-dark-text hover:text-gray-900 dark:hover:text-white transition-colors mr-4"
        >
          <ArrowLeft class="w-5 h-5 mr-2" />
          Back to Exams
        </button>
      </div>
      <h1 class="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-2">
        Edit Exam
      </h1>
      <p class="text-secondary-text dark:text-secondary-dark-text">
        Update examination content, questions, and settings.
      </p>
    </div>

    <!-- Loading State -->
    <div v-if="loading && !exam" class="mt-6 flex flex-col justify-center items-center py-12">
      <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-4"></div>
      <span class="text-gray-900 dark:text-white">Loading exam...</span>
    </div>

    <!-- Error State -->
    <div
      v-else-if="error"
      class="mt-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6"
    >
      <div class="flex items-center">
        <AlertCircle class="h-5 w-5 text-red-500 mr-2" />
        <span class="text-red-700 dark:text-red-300 font-medium">{{ error }}</span>
      </div>
      <button
        @click="loadExam"
        class="mt-2 text-sm text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200 underline"
      >
        Try again
      </button>
    </div>

    <!-- Edit Exam Form -->
    <div v-else-if="exam" class="grid grid-cols-1 xl:grid-cols-3 gap-6">
      <!-- Main Form -->
      <div class="xl:col-span-2 space-y-6">
        <!-- Basic Information -->
        <div class="card">
          <div class="card-header">
            <div class="flex items-center">
              <div class="w-8 h-8 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center mr-3">
                <FileText class="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
                Basic Information
              </h3>
            </div>
          </div>
          <div class="card-body space-y-4">
            <div>
              <label class="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                Exam Name <span class="text-red-500">*</span>
              </label>
              <input
                v-model="examForm.exam_name"
                type="text"
                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-primary transition duration-200"
                placeholder="Enter exam name"
              />
            </div>

            <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                  Passing Rate (%) <span class="text-red-500">*</span>
                </label>
                <input
                  v-model.number="examForm.passing_rate"
                  type="number"
                  min="0"
                  max="100"
                  class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-primary transition duration-200"
                  placeholder="70"
                />
              </div>
              
              <div>
                <label class="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                  Time Limit (minutes)
                </label>
                <input
                  v-model.number="examForm.time_limit"
                  type="number"
                  min="1"
                  max="1440"
                  step="1"
                  @input="handleTimeLimitInput"
                  @keypress="preventDecimalInput"
                  class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-primary transition duration-200"
                  placeholder="No time limit"
                />
                <p class="text-xs text-secondary-text dark:text-secondary-dark-text mt-1">
                  Leave empty for no time limit (max: 1440 minutes)
                </p>
              </div>

              <div>
                <label class="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                  Max Attempts
                </label>
                <input
                  v-model.number="examForm.total_attempts"
                  type="number"
                  min="1"
                  max="10"
                  step="1"
                  @input="handleTotalAttemptsInput"
                  @keypress="preventDecimalInput"
                  class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-primary transition duration-200"
                  placeholder="Unlimited"
                />
                <p class="text-xs text-secondary-text dark:text-secondary-dark-text mt-1">
                  Leave empty for unlimited (max: 10 attempts)
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Questions Section -->
        <div class="card">
          <div class="card-header">
            <div class="flex items-center justify-between">
              <div class="flex items-center">
                <div class="w-8 h-8 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center mr-3">
                  <HelpCircle class="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
                    Questions ({{ examForm.exam_content.questions.length }})
                  </h3>
                  <p class="text-xs text-secondary-text dark:text-secondary-dark-text mt-1">
                    Edit questions for this exam
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div class="card-body space-y-6">
            <!-- Questions List -->
            <div v-if="examForm.exam_content.questions.length === 0" class="text-center py-8">
              <div class="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mx-auto mb-4">
                <HelpCircle class="h-8 w-8 text-gray-400 dark:text-gray-500" />
              </div>
              <h3 class="text-lg font-medium text-gray-900 dark:text-white mb-2">No questions found</h3>
              <p class="text-gray-500 dark:text-gray-400">This exam has no questions.</p>
            </div>

            <!-- Question Cards -->
            <div v-for="(question, index) in examForm.exam_content.questions" :key="question.id" 
                 :class="[
                   'border rounded-lg p-4 space-y-4',
                   incompleteQuestions[index]?.isComplete 
                     ? 'border-green-200 dark:border-green-700 bg-green-50 dark:bg-green-900/10' 
                     : 'border-red-200 dark:border-red-700 bg-red-50 dark:bg-red-900/10'
                 ]">
              <div class="flex items-center justify-between">
                <div class="flex items-center gap-2">
                  <h4 class="text-lg font-medium text-gray-900 dark:text-white">
                    Question {{ index + 1 }}
                  </h4>
                  <span v-if="incompleteQuestions[index]?.isComplete" class="text-xs px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full">
                    Complete
                  </span>
                  <span v-else class="text-xs px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-full">
                    Incomplete
                  </span>
                </div>
              </div>

              <!-- Show validation issues if question is incomplete -->
              <div v-if="!incompleteQuestions[index]?.isComplete" class="p-2 bg-red-100 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-sm">
                <p class="text-red-700 dark:text-red-300 font-medium mb-1">Required Fields:</p>
                <ul class="text-red-600 dark:text-red-400 text-xs space-y-1">
                  <li v-for="issue in incompleteQuestions[index]?.issues" :key="issue" class="flex items-center gap-1">
                    <span class="w-1 h-1 bg-red-500 rounded-full"></span>
                    {{ issue }}
                  </li>
                </ul>
              </div>

              <!-- Question Type -->
              <div>
                <label class="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                  Question Type
                </label>
                <select
                  v-model="question.type"
                  @change="updateQuestionType(question)"
                  class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-primary transition duration-200"
                >
                  <option value="multiple_choice">Multiple Choice</option>
                  <option value="true_false">True/False</option>
                  <option value="essay">Essay</option>
                </select>
              </div>

              <!-- Question Text -->
              <div>
                <label class="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                  Question Text <span class="text-red-500">*</span>
                </label>
                <textarea
                  v-model="question.question"
                  rows="3"
                  class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-primary transition duration-200"
                  placeholder="Enter your question..."
                />
              </div>

              <!-- Multiple Choice Options -->
              <div v-if="question.type === 'multiple_choice' && question.options">
                <label class="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                  Answer Options <span class="text-red-500">*</span>
                </label>
                <p class="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  Fill options in order (A, then B, then C, etc.). Empty options with filled options after them will show validation errors.
                </p>
                <div class="space-y-3">
                  <div v-for="(option, optIndex) in question.options" :key="option.id" 
                       class="radio-option-wrapper"
                       :class="{ 'selected': getAnswerValue(question.id) === option.id }">
                    <label class="radio-option-label" :for="`option_${question.id}_${option.id}`">
                      <div class="radio-option-content">
                        <div class="radio-button-wrapper">
                          <input
                            :id="`option_${question.id}_${option.id}`"
                            type="radio"
                            :name="`correct_${question.id}`"
                            :value="option.id"
                            :checked="getAnswerValue(question.id) === option.id"
                            @change="setAnswerValue(question.id, option.id)"
                            class="radio-input"
                          />
                          <div class="radio-button">
                            <Check class="w-3 h-3" v-if="getAnswerValue(question.id) === option.id" />
                          </div>
                        </div>
                        <span class="option-letter">
                          {{ String.fromCharCode(65 + optIndex) }}.
                        </span>
                        <input
                          v-model="option.text"
                          type="text"
                          :class="[
                            'flex-1 px-3 py-2 border rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-primary transition duration-200',
                            getOptionValidationClass(question, optIndex)
                          ]"
                          :placeholder="`Option ${String.fromCharCode(65 + optIndex)}`"
                        />
                        <div v-if="question.options && question.options.length > 2" class="flex items-center">
                          <!-- Icon-only remove button for larger screens -->
                          <button
                            @click="removeOption(question, optIndex)"
                            class="remove-icon-btn text-red-500 hover:text-red-700 transition-colors hidden sm:inline-flex"
                            type="button"
                            aria-label="Remove option"
                          >
                            <X class="w-4 h-4" />
                          </button>

                          <!-- Text+icon remove button for small screens -->
                          <button
                            @click="removeOption(question, optIndex)"
                            class="remove-text-btn text-red-500 hover:text-red-700 transition-colors inline-flex sm:hidden items-center px-2 py-1 text-sm rounded hover:underline"
                            type="button"
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    </label>
                  </div>
                  <button
                    v-if="question.options && question.options.length < 6"
                    @click="addOption(question)"
                    type="button"
                    class="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 flex items-center"
                  >
                    <Plus class="w-4 h-4 mr-1" />
                    Add Option
                  </button>
                </div>
              </div>

              <!-- True/False Options -->
              <div v-else-if="question.type === 'true_false'">
                <label class="block text-sm font-medium text-gray-900 dark:text-white mb-3">
                  Correct Answer <span class="text-red-500">*</span>
                </label>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <label class="true-false-option" :for="`true_${question.id}`" 
                         :class="{ 'selected': getAnswerValue(question.id) === 'true' }">
                    <input
                      :id="`true_${question.id}`"
                      type="radio"
                      :name="`correct_${question.id}`"
                      value="true"
                      :checked="getAnswerValue(question.id) === 'true'"
                      @change="setAnswerValue(question.id, 'true')"
                      class="radio-input"
                    />
                    <div class="true-false-content">
                      <div class="true-false-icon">
                        <Check class="w-4 h-4" v-if="getAnswerValue(question.id) === 'true'" />
                      </div>
                      <span class="true-false-text">True</span>
                    </div>
                  </label>
                  <label class="true-false-option" :for="`false_${question.id}`"
                         :class="{ 'selected': getAnswerValue(question.id) === 'false' }">
                    <input
                      :id="`false_${question.id}`"
                      type="radio"
                      :name="`correct_${question.id}`"
                      value="false"
                      :checked="getAnswerValue(question.id) === 'false'"
                      @change="setAnswerValue(question.id, 'false')"
                      class="radio-input"
                    />
                    <div class="true-false-content">
                      <div class="true-false-icon">
                        <Check class="w-4 h-4" v-if="getAnswerValue(question.id) === 'false'" />
                      </div>
                      <span class="true-false-text">False</span>
                    </div>
                  </label>
                </div>
              </div>

              <!-- Essay Answer Key -->
              <div v-else-if="question.type === 'essay'">
                <label class="block text-sm font-medium text-gray-900 dark:text-white mb-2">
                  Keywords / Key Points <span class="text-red-500">*</span>
                </label>
                <KeywordInput
                  :model-value="getEssayKeywords(question.id)"
                  @update:model-value="updateEssayKeywords(question.id, $event)"
                  placeholder="Enter keywords or key points for this essay question..."
                />
              </div>
            </div>
            <div v-if="examForm.exam_content.questions.length >= 3" class="text-center mt-4">
              <button
                @click="scrollToTop"
                type="button"
                class="inline-flex items-center text-sm text-gray-900 dark:text-white hover:underline"
              >
                <ArrowUp class="w-4 h-4 mr-2" />
                Back to top
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Sidebar -->
      <div class="space-y-6">
        <div class="sticky-sidebar">
        <!-- Preview Card -->
        <div class="card">
          <div class="card-header">
            <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
              Exam Preview
            </h3>
          </div>
          <div class="card-body space-y-4">
            <div class="flex items-center justify-between">
              <span class="text-sm text-secondary-text dark:text-secondary-dark-text">Questions</span>
              <span class="font-medium text-gray-900 dark:text-white">
                {{ examForm.exam_content.questions.length }}
              </span>
            </div>
            <div class="flex items-center justify-between">
              <span class="text-sm text-secondary-text dark:text-secondary-dark-text">Complete Questions</span>
              <span class="font-medium text-gray-900 dark:text-white">
                {{ incompleteQuestions.filter(q => q.isComplete).length }} / {{ incompleteQuestions.length }}
              </span>
            </div>
            <div class="flex items-center justify-between">
              <span class="text-sm text-secondary-text dark:text-secondary-dark-text">Passing Rate</span>
              <span class="font-medium text-gray-900 dark:text-white">
                {{ examForm.passing_rate || 0 }}%
              </span>
            </div>
            <div class="flex items-center justify-between">
              <span class="text-sm text-secondary-text dark:text-secondary-dark-text">Time Limit</span>
              <span class="font-medium text-gray-900 dark:text-white">
                {{ examForm.time_limit ? `${examForm.time_limit} min` : 'No limit' }}
              </span>
            </div>
            <div class="flex items-center justify-between">
              <span class="text-sm text-secondary-text dark:text-secondary-dark-text">Max Attempts</span>
              <span class="font-medium text-gray-900 dark:text-white">
                {{ examForm.total_attempts ? `${examForm.total_attempts}` : 'Unlimited' }}
              </span>
            </div>
            
            <!-- Validation Status -->
            <div class="pt-3 border-t border-gray-200 dark:border-gray-700">
              <div class="flex items-center justify-between mb-2">
                <span class="text-sm font-medium text-gray-900 dark:text-white">Status</span>
                <span v-if="canSaveExam" class="text-xs px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-full">
                  Ready to Save
                </span>
                <span v-else class="text-xs px-2 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-full">
                  Not Ready
                </span>
              </div>
              <div v-if="!canSaveExam" class="text-xs text-red-600 dark:text-red-400 space-y-1">
                <p v-if="!hasFormChanged">• No changes detected</p>
                <p v-if="!examForm.exam_name || !examForm.exam_name.trim()">• Enter exam name</p>
                <p v-if="!examForm.passing_rate || examForm.passing_rate <= 0 || examForm.passing_rate > 100">• Set valid passing rate (1-100%)</p>
                <p v-if="!incompleteQuestions.some(q => q.isComplete)">• Complete at least one question</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Action Buttons -->
        <div class="mt-2 card">
          <div class="card-body space-y-3">
            <button
              @click="updateExam"
              :disabled="!canSaveExam || updating"
              class="w-full btn btn-primary"
            >
              <Save class="w-4 h-4 mr-2" />
              {{ updating ? 'Updating...' : 'Update Exam' }}
            </button>
            <button
              @click="updateExamAsDraft"
              :disabled="!canSaveAsDraft || updating"
              class="w-full btn btn-secondary"
            >
              <FileText class="w-4 h-4 mr-2" />
              {{ updating ? 'Updating...' : 'Update as Draft' }}
            </button>
            <button
              @click="handleBack"
              class="w-full btn btn-outline"
            >
              Cancel
            </button>
          </div>
        </div>
        </div>
      </div>
    </div>

    <!-- Incomplete Questions Confirmation Modal -->
    <IncompleteQuestionsModal
      :is-visible="showIncompleteModal"
      :incomplete-questions="modalQuestionsData"
      @close="showIncompleteModal = false"
      @confirm="proceedWithUpdate"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useTitle } from '@/composables/ui/useTitle'
import { useToastService } from '@/services/toast'
import { examService } from '@/services/examService'
import type { Question, Exam, UpdateExamPayload } from '@/services/examService'
import IncompleteQuestionsModal from '@/components/modals/IncompleteQuestionsModal.vue'
import KeywordInput from '@/components/forms/KeywordInput.vue'
import {
  FileText,
  ArrowLeft,
  HelpCircle,
  Save,
  AlertCircle,
  X,
  Check,
  ArrowUp
} from 'lucide-vue-next'

// Props and Emits
interface Props {
  examId: number
}

const props = defineProps<Props>()

const emit = defineEmits<{
  back: []
  examUpdated: []
}>()

// Page title
const appTitle = computed(() => process.env.VUE_APP_TITLE || 'Douglas Private Care Services')
const { setTitle } = useTitle(`Edit Exam - ${appTitle.value}`)

const handleBack = () => {
  setTitle(`Exams - ${appTitle.value}`)
  emit('back')
}

// Services
const toastService = useToastService()

// Helper function to maintain backward compatibility
const showToast = (message: string, type: 'success' | 'error' | 'info' = 'info') => {
  if (type === 'success') {
    toastService.success(message)
  } else if (type === 'error') {
    toastService.error(message)
  } else {
    toastService.success(message) // Use success for info since the service may not have info
  }
}

// State
const loading = ref(false)
const updating = ref(false)
const error = ref('')
const exam = ref<Exam | null>(null)
const showIncompleteModal = ref(false)
const pendingUpdateType = ref<'exam' | 'draft'>('exam')
const originalExamData = ref<string>('') // Store original exam data as JSON string for comparison

// Form state - will be populated when exam loads
const examForm = ref<UpdateExamPayload & { exam_content: { questions: Question[] } }>({
  exam_name: '',
  exam_content: {
    questions: []
  },
  answer_key: {},
  passing_rate: 70,
  time_limit: null,
  total_attempts: 3,
  exam_status: 1
})

// Computed properties
const hasFormChanged = computed(() => {
  if (!originalExamData.value) return false
  
  // Create a comparable version of current form data
  const currentData = JSON.stringify({
    exam_name: examForm.value.exam_name,
    exam_content: examForm.value.exam_content,
    answer_key: examForm.value.answer_key,
    passing_rate: examForm.value.passing_rate,
    time_limit: examForm.value.time_limit,
    total_attempts: examForm.value.total_attempts,
    exam_status: examForm.value.exam_status
  })
  
  return currentData !== originalExamData.value
})

const canSaveExam = computed(() => {
  // Check if any changes were made (only for "Update Exam", not for draft)
  if (!hasFormChanged.value) {
    return false
  }

  // Check if exam name is filled
  if (!examForm.value.exam_name || !examForm.value.exam_name.trim()) {
    return false
  }

  // Check if passing rate is set (should be between 1 and 100)
  if (!examForm.value.passing_rate || examForm.value.passing_rate <= 0 || examForm.value.passing_rate > 100) {
    return false
  }

  // Check if there are any questions
  if (examForm.value.exam_content.questions.length === 0) {
    return false
  }

  // Check if at least one question is properly filled
  const hasValidQuestion = examForm.value.exam_content.questions.some((question: Question) => {
    // Question text must be filled
    if (!question.question.trim()) {
      return false
    }

    // Check based on question type
    if (question.type === 'multiple_choice') {
      // Must have at least 2 options with text, and a correct answer selected
      const validOptions = question.options?.filter(option => option.text && option.text.trim()) || []
      const hasCorrectAnswer = examForm.value.answer_key && examForm.value.answer_key[question.id]
      return validOptions.length >= 2 && hasCorrectAnswer
    } else if (question.type === 'true_false') {
      // Must have a correct answer selected
      return examForm.value.answer_key && examForm.value.answer_key[question.id] !== undefined && examForm.value.answer_key[question.id] !== ''
    } else if (question.type === 'essay') {
      // Must have at least one keyword
      const keywords = getEssayKeywords(question.id)
      return keywords.length > 0
    }

    return false
  })

  return hasValidQuestion
})

const canSaveAsDraft = computed(() => {
  // For draft, we don't require changes - just basic validation
  // Check if exam name is filled
  if (!examForm.value.exam_name || !examForm.value.exam_name.trim()) {
    return false
  }

  // Check if passing rate is set (should be between 1 and 100)
  if (!examForm.value.passing_rate || examForm.value.passing_rate <= 0 || examForm.value.passing_rate > 100) {
    return false
  }

  // Check if there are any questions
  if (examForm.value.exam_content.questions.length === 0) {
    return false
  }

  // Check if at least one question is properly filled
  const hasValidQuestion = examForm.value.exam_content.questions.some((question: Question) => {
    // Question text must be filled
    if (!question.question.trim()) {
      return false
    }

    // Check based on question type
    if (question.type === 'multiple_choice') {
      // Must have at least 2 options with text, and a correct answer selected
      const validOptions = question.options?.filter(option => option.text && option.text.trim()) || []
      const hasCorrectAnswer = examForm.value.answer_key && examForm.value.answer_key[question.id]
      return validOptions.length >= 2 && hasCorrectAnswer
    } else if (question.type === 'true_false') {
      // Must have a correct answer selected
      return examForm.value.answer_key && examForm.value.answer_key[question.id] !== undefined && examForm.value.answer_key[question.id] !== ''
    } else if (question.type === 'essay') {
      // Must have at least one keyword
      const keywords = getEssayKeywords(question.id)
      return keywords.length > 0
    }

    return false
  })

  return hasValidQuestion
})

// Helper computed to track which questions are incomplete
const incompleteQuestions = computed(() => {
  return examForm.value.exam_content.questions.map((question: Question, index: number) => {
    const issues: string[] = []
    
    // Check if question text is filled
    if (!question.question.trim()) {
      issues.push('Question text is required')
    }
    
    // Check based on question type
    if (question.type === 'multiple_choice') {
      const validOptions = question.options?.filter(option => option.text && option.text.trim()) || []
      
      // Check if options are filled in order (no gaps)
      if (question.options) {
        for (let i = 0; i < question.options.length; i++) {
          const currentOption = question.options[i]
          const isEmpty = !currentOption.text || !currentOption.text.trim()
          
          if (isEmpty) {
            // Check if there are filled options after this empty one
            const hasFilledAfter = question.options.slice(i + 1).some(opt => opt.text && opt.text.trim())
            if (hasFilledAfter) {
              issues.push(`Option ${String.fromCharCode(65 + i)} must be filled before later options`)
            }
          }
        }
      }
      
      if (validOptions.length < 2) {
        issues.push('At least 2 options must be filled')
      }
      if (!examForm.value.answer_key || !examForm.value.answer_key[question.id]) {
        issues.push('Correct answer must be selected')
      }
    } else if (question.type === 'true_false') {
      if (!examForm.value.answer_key || examForm.value.answer_key[question.id] === undefined || examForm.value.answer_key[question.id] === '') {
        issues.push('Correct answer must be selected')
      }
    } else if (question.type === 'essay') {
      const keywords = getEssayKeywords(question.id)
      if (keywords.length === 0) {
        issues.push('At least one keyword is required')
      }
    }
    
    return {
      index,
      isComplete: issues.length === 0,
      issues
    }
  })
})

// Helper function to check if a question is complete
const isQuestionComplete = (question: Question): boolean => {
  // Check if question text is filled
  if (!question.question.trim()) {
    return false
  }
  
  // Check based on question type
  if (question.type === 'multiple_choice') {
    const validOptions = question.options?.filter(option => option.text && option.text.trim()) || []
    
    // Check if options are filled in order (no gaps)
    if (question.options) {
      for (let i = 0; i < question.options.length; i++) {
        const currentOption = question.options[i]
        const isEmpty = !currentOption.text || !currentOption.text.trim()
        
        if (isEmpty) {
          // Check if there are filled options after this empty one
          const hasFilledAfter = question.options.slice(i + 1).some(opt => opt.text && opt.text.trim())
          if (hasFilledAfter) {
            return false // Has gaps in options
          }
        }
      }
    }
    
    if (validOptions.length < 2) {
      return false
    }
    if (!examForm.value.answer_key || !examForm.value.answer_key[question.id]) {
      return false
    }
  } else if (question.type === 'true_false') {
    if (!examForm.value.answer_key || examForm.value.answer_key[question.id] === undefined || examForm.value.answer_key[question.id] === '') {
      return false
    }
  } else if (question.type === 'essay') {
    const keywords = getEssayKeywords(question.id)
    if (keywords.length === 0) {
      return false
    }
  }
  
  return true
}

// Scroll helper to go back to top of this view
const scrollToTop = () => {
  const el = document.querySelector('.edit-exam-view') as HTMLElement | null
  if (el) {
    el.scrollIntoView({ behavior: 'smooth' })
  } else {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }
}

// Computed property for modal data - reuses incompleteQuestions logic
const modalQuestionsData = computed(() => {
  return incompleteQuestions.value.map((questionData) => ({
    index: questionData.index,
    question: examForm.value.exam_content.questions[questionData.index]?.question || '',
    isComplete: questionData.isComplete,
    issues: questionData.issues
  }))
})

// Methods
const loadExam = async () => {
  try {
    loading.value = true
    error.value = ''
    
    const response = await examService.getExam(props.examId)
    exam.value = response.data.data
    
    // Populate form with exam data
    examForm.value = {
      exam_name: exam.value.exam_name,
      exam_content: {
        questions: [...exam.value.exam_content.questions] // Deep copy
      },
      answer_key: { ...exam.value.answer_key }, // Deep copy
      passing_rate: exam.value.passing_rate,
      time_limit: exam.value.time_limit,
      total_attempts: exam.value.total_attempts,
      exam_status: exam.value.exam_status
    }
    
    // Convert legacy essay data (strings) to new format (arrays)
    examForm.value.exam_content.questions.forEach(question => {
      if (question.type === 'essay' && examForm.value.answer_key && examForm.value.answer_key[question.id]) {
        const essayData = examForm.value.answer_key[question.id]
        if (typeof essayData === 'string' && essayData.trim()) {
          // Convert string to array of keywords by splitting on common delimiters
          const keywords = essayData
            .split(/[,;.\n]/)
            .map(keyword => keyword.trim())
            .filter(keyword => keyword !== '')
          examForm.value.answer_key[question.id] = keywords
        } else if (!Array.isArray(essayData)) {
          // Initialize as empty array if not already an array
          examForm.value.answer_key[question.id] = []
        }
      }
    })
    
    
    // Store original exam data for change detection
    originalExamData.value = JSON.stringify({
      exam_name: examForm.value.exam_name,
      exam_content: examForm.value.exam_content,
      answer_key: examForm.value.answer_key,
      passing_rate: examForm.value.passing_rate,
      time_limit: examForm.value.time_limit,
      total_attempts: examForm.value.total_attempts,
      exam_status: examForm.value.exam_status
    })
    
  } catch (err: any) {
    console.error('Error loading exam:', err)
    error.value = err.response?.data?.message || err.message || 'Failed to load exam'
    showToast('Failed to load exam', 'error')
  } finally {
    loading.value = false
  }
}

// Exam form methods
const updateQuestionType = (question: Question) => {
  // Reset answer key when question type changes
  if (examForm.value.answer_key && examForm.value.answer_key[question.id]) {
    delete examForm.value.answer_key[question.id]
  }
  
  // Initialize answer key based on type
  if (question.type === 'essay') {
    if (!examForm.value.answer_key) {
      examForm.value.answer_key = {}
    }
    examForm.value.answer_key[question.id] = []
  }
  
  // Update options based on type
  if (question.type === 'multiple_choice') {
    question.options = [
      { id: 'a', text: '' },
      { id: 'b', text: '' },
      { id: 'c', text: '' },
      { id: 'd', text: '' }
    ]
  } else {
    delete (question as any).options
  }
}

const addOption = (question: Question) => {
  if (question.options) {
    // Check if all current options are filled before adding a new one
    const hasEmptyOptions = question.options.some(option => !option.text || !option.text.trim())
    
    if (hasEmptyOptions) {
      showToast('Please fill all current options before adding a new one', 'info')
      return
    }
    
    const nextLetter = String.fromCharCode(97 + question.options.length) // a, b, c, d, e, f
    question.options.push({
      id: nextLetter,
      text: ''
    })
  }
}

const removeOption = (question: Question, index: number) => {
  if (question.options && question.options.length > 2) {
    question.options.splice(index, 1)
    // Update option IDs to maintain a, b, c, d sequence
    question.options.forEach((option: { id: string; text: string }, idx: number) => {
      option.id = String.fromCharCode(97 + idx)
    })
    
    // Reset answer key if the selected option was removed
    const answerKey = getAnswerValue(question.id)
    const stillExists = question.options.some(opt => opt.id === answerKey)
    if (!stillExists) {
      setAnswerValue(question.id, '')
    }
  }
}

// Helper function to get validation classes for options
const getOptionValidationClass = (question: Question, optIndex: number) => {
  if (!question.options) return 'border-gray-300 dark:border-gray-600'
  
  const option = question.options[optIndex]
  const isEmpty = !option.text || !option.text.trim()
  
  if (isEmpty) {
    // Check if there are filled options after this empty one
    const hasFilledAfter = question.options.slice(optIndex + 1).some(opt => opt.text && opt.text.trim())
    if (hasFilledAfter) {
      return 'border-red-500 dark:border-red-500 bg-red-50 dark:bg-red-900/10'
    }
    return 'border-gray-300 dark:border-gray-600'
  }
  
  return 'border-green-500 dark:border-green-500 bg-green-50 dark:bg-green-900/10'
}

// Helper functions for answer key management
const getAnswerValue = (questionId: number) => {
  if (!examForm.value.answer_key) return ''
  return examForm.value.answer_key[questionId] || ''
}

const setAnswerValue = (questionId: number, value: any) => {
  if (!examForm.value.answer_key) {
    examForm.value.answer_key = {}
  }
  examForm.value.answer_key[questionId] = value
}

// Helper functions for essay keywords
const getEssayKeywords = (questionId: number): string[] => {
  if (!examForm.value.answer_key) return []
  const keywords = examForm.value.answer_key[questionId]
  return Array.isArray(keywords) ? keywords : []
}

const updateEssayKeywords = (questionId: number, keywords: string[]) => {
  if (!examForm.value.answer_key) {
    examForm.value.answer_key = {}
  }
  examForm.value.answer_key[questionId] = keywords
}

// Input validation functions
const preventDecimalInput = (event: KeyboardEvent) => {
  // Prevent decimal point and minus sign
  if (event.key === '.' || event.key === ',' || event.key === '-' || event.key === 'e' || event.key === 'E') {
    event.preventDefault()
  }
}

const handleTimeLimitInput = (event: Event) => {
  const target = event.target as HTMLInputElement
  let value = target.value
  
  // If empty, set to null (unlimited)
  if (value === '' || value === null) {
    examForm.value.time_limit = null
    return
  }
  
  // Remove any non-digit characters
  value = value.replace(/[^\d]/g, '')
  
  // Convert to number
  let numValue = parseInt(value, 10)
  
  // If NaN, set to null
  if (isNaN(numValue)) {
    examForm.value.time_limit = null
    target.value = ''
    return
  }
  
  // Enforce min and max
  if (numValue < 1) {
    numValue = 1
  } else if (numValue > 1440) {
    numValue = 1440
  }
  
  examForm.value.time_limit = numValue
  target.value = numValue.toString()
}

const handleTotalAttemptsInput = (event: Event) => {
  const target = event.target as HTMLInputElement
  let value = target.value
  
  // If empty, set to null (unlimited)
  if (value === '' || value === null) {
    examForm.value.total_attempts = null
    return
  }
  
  // Remove any non-digit characters
  value = value.replace(/[^\d]/g, '')
  
  // Convert to number
  let numValue = parseInt(value, 10)
  
  // If NaN, set to null
  if (isNaN(numValue)) {
    examForm.value.total_attempts = null
    target.value = ''
    return
  }
  
  // Enforce min and max
  if (numValue < 1) {
    numValue = 1
  } else if (numValue > 10) {
    numValue = 10
  }
  
  examForm.value.total_attempts = numValue
  target.value = numValue.toString()
}

// Helper function to clean exam data before sending
const cleanExamData = (data: UpdateExamPayload & { exam_content: { questions: Question[] } }) => {
  const cleanedData = JSON.parse(JSON.stringify(data)) // Deep clone
  
  // Filter out incomplete questions first, then clean up the remaining complete questions
  cleanedData.exam_content.questions = cleanedData.exam_content.questions
    .filter((question: Question) => isQuestionComplete(question))
    .map((question: Question) => {
      const cleanedQuestion = { ...question }
      
      // For multiple choice questions, filter out empty options
      if (question.type === 'multiple_choice' && question.options) {
        cleanedQuestion.options = question.options.filter(option => 
          option.text && option.text.trim() !== ''
        )
        
        // Re-assign option IDs to maintain sequence (a, b, c, etc.)
        cleanedQuestion.options.forEach((option: { id: string; text: string }, idx: number) => {
          option.id = String.fromCharCode(97 + idx) // a, b, c, d, e, f
        })
      }
      
      // Clean question text
      cleanedQuestion.question = question.question.trim()
      
      return cleanedQuestion
    })

  // Clean answer key - only keep answers for questions that exist and are valid
  const cleanedAnswerKey: Record<string, any> = {}
  cleanedData.exam_content.questions.forEach((question: Question) => {
    if (data.answer_key && data.answer_key[question.id] !== undefined && data.answer_key[question.id] !== '') {
      // For multiple choice, make sure the answer references a valid option
      if (question.type === 'multiple_choice') {
        const validOptionIds = question.options?.map(opt => opt.id) || []
        if (validOptionIds.includes(data.answer_key[question.id])) {
          cleanedAnswerKey[question.id] = data.answer_key[question.id]
        }
      } else {
        cleanedAnswerKey[question.id] = data.answer_key[question.id]
      }
    }
  })

  return {
    ...cleanedData,
    exam_content: {
      questions: cleanedData.exam_content.questions
    },
    answer_key: cleanedAnswerKey,
    exam_name: data.exam_name ? data.exam_name.trim() : ''
  }
}

// Save methods
const updateExam = async () => {
  // Check if exam name is filled
  if (!examForm.value.exam_name || !examForm.value.exam_name.trim()) {
    showToast('Please enter an exam name before saving', 'error')
    return
  }

  // Check if passing rate is set
  if (!examForm.value.passing_rate || examForm.value.passing_rate <= 0 || examForm.value.passing_rate > 100) {
    showToast('Please set a valid passing rate (1-100%)', 'error')
    return
  }

  // Check if there are any questions
  if (examForm.value.exam_content.questions.length === 0) {
    showToast('Please add at least one question before saving', 'error')
    return
  }

  // Check if there are any complete questions
  const hasCompleteQuestion = incompleteQuestions.value.some(q => q.isComplete)
  if (!hasCompleteQuestion) {
    showToast('Please complete at least one question before saving', 'error')
    return
  }

  // Check if there are incomplete questions
  const hasIncompleteQuestions = incompleteQuestions.value.some(q => !q.isComplete)
  if (hasIncompleteQuestions) {
    pendingUpdateType.value = 'exam'
    showIncompleteModal.value = true
    return
  }

  // If all questions are complete, proceed directly
  await performUpdate('exam')
}

const updateExamAsDraft = async () => {
  // Check if exam name is filled
  if (!examForm.value.exam_name || !examForm.value.exam_name.trim()) {
    showToast('Please enter an exam name before saving', 'error')
    return
  }

  // Check if passing rate is set
  if (!examForm.value.passing_rate || examForm.value.passing_rate <= 0 || examForm.value.passing_rate > 100) {
    showToast('Please set a valid passing rate (1-100%)', 'error')
    return
  }

  // Check if there are any questions
  if (examForm.value.exam_content.questions.length === 0) {
    showToast('Please add at least one question before saving', 'error')
    return
  }

  // Check if there are any complete questions
  const hasCompleteQuestion = incompleteQuestions.value.some(q => q.isComplete)
  if (!hasCompleteQuestion) {
    showToast('Please complete at least one question before saving', 'error')
    return
  }

  // Check if there are incomplete questions
  const hasIncompleteQuestions = incompleteQuestions.value.some(q => !q.isComplete)
  if (hasIncompleteQuestions) {
    pendingUpdateType.value = 'draft'
    showIncompleteModal.value = true
    return
  }

  // If all questions are complete, proceed directly
  await performUpdate('draft')
}

const proceedWithUpdate = async () => {
  showIncompleteModal.value = false
  await performUpdate(pendingUpdateType.value)
}

const performUpdate = async (type: 'exam' | 'draft') => {
  try {
    updating.value = true
    const cleanedExamData = cleanExamData(examForm.value)
    cleanedExamData.exam_status = type === 'exam' ? 1 : 0
    
    await examService.updateExam(props.examId, cleanedExamData)
    showToast(`Exam ${type === 'exam' ? 'updated' : 'updated as draft'} successfully!`, 'success')
    
    emit('examUpdated')
    emit('back')
  } catch (err: any) {
    console.error(`Error ${type === 'exam' ? 'updating' : 'updating'} exam:`, err)
    showToast(err.response?.data?.message || `Failed to ${type === 'exam' ? 'update' : 'update'} exam`, 'error')
  } finally {
    updating.value = false
  }
}

// Lifecycle
onMounted(() => {
  loadExam()
})
</script>

<style scoped>
.card {
  background-color: white;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
  border: 1px solid #e5e7eb;
}

.dark .card {
  background-color: #1f2937;
  border-color: #374151;
}

.card-header {
  padding: 1.5rem 1.5rem 0 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 1rem;
  margin-bottom: 1.5rem;
}

.dark .card-header {
  border-bottom-color: #374151;
}

.card-body {
  padding: 1.5rem;
}

/* Exam view container animation */
.exam-view-container {
  animation: slideInUp 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.sticky-sidebar {
  position: sticky;
  top: 4.5rem; /* offset from top to account for header/controls */
  display: block;
}

@media (max-width: 1279px) {
  /* On smaller screens the sidebar should not be sticky */
  .sticky-sidebar {
    position: static;
    top: auto;
  }
}

@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Stagger animation for child elements */
.exam-view-container > * {
  animation: fadeInStagger 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) both;
}

.exam-view-container > *:nth-child(1) { animation-delay: 0.1s; }
.exam-view-container > *:nth-child(2) { animation-delay: 0.15s; }
.exam-view-container > *:nth-child(3) { animation-delay: 0.2s; }
.exam-view-container > *:nth-child(4) { animation-delay: 0.25s; }

@keyframes fadeInStagger {
  from {
    opacity: 0;
    transform: translateY(15px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Modern Radio Button Styles */
.radio-option-wrapper {
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  padding: 0.75rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  margin-bottom: 0.5rem;
}

.dark .radio-option-wrapper {
  border-color: #4b5563;
}

.radio-option-wrapper:hover {
  border-color: #d1d5db;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.06);
  transform: translateY(-1px);
}

.dark .radio-option-wrapper:hover {
  border-color: #6b7280;
}

.radio-option-wrapper.selected {
  border-color: #3b82f6;
  background-color: #eff6ff;
  transform: scale(1.01);
}

.dark .radio-option-wrapper.selected {
  background-color: rgba(59, 130, 246, 0.1);
  border-color: #60a5fa;
}

.radio-option-label {
  cursor: pointer;
  display: flex;
  align-items: center;
  width: 100%;
  gap: 0.75rem;
}

.radio-option-content {
  display: flex;
  align-items: center;
  flex: 1;
  gap: 0.75rem;
}

.radio-button-wrapper {
  position: relative;
  flex-shrink: 0;
}

.radio-input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}

.radio-button {
  width: 1.25rem;
  height: 1.25rem;
  border: 2px solid #d1d5db;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  background-color: #f9fafb;
}

.dark .radio-button {
  border-color: #4b5563;
  background-color: #374151;
}

.radio-option-wrapper.selected .radio-button {
  border-color: #3b82f6;
  background-color: #3b82f6;
  color: white;
}

.dark .radio-option-wrapper.selected .radio-button {
  border-color: #60a5fa;
  background-color: #60a5fa;
}

.radio-input:focus + .radio-button {
  outline: 2px solid #dbeafe;
  outline-offset: 2px;
}

.dark .radio-input:focus + .radio-button {
  outline-color: rgba(59, 130, 246, 0.3);
}

.option-letter {
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  min-width: 1.5rem;
}

.dark .option-letter {
  color: #d1d5db;
}

.radio-option-wrapper.selected .option-letter {
  color: #1e40af;
}

.dark .radio-option-wrapper.selected .option-letter {
  color: #93c5fd;
}

/* True/False Radio Button Styles */
.true-false-option {
  cursor: pointer;
  display: block;
}

.true-false-option.selected .true-false-content {
  border-color: #3b82f6;
  background-color: #eff6ff;
  transform: scale(1.02);
}

.dark .true-false-option.selected .true-false-content {
  background-color: rgba(59, 130, 246, 0.15);
  border-color: #60a5fa;
}

.true-false-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 1rem;
  border: 2px solid #e5e7eb;
  border-radius: 0.5rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  min-height: 3.5rem;
}

.dark .true-false-content {
  border-color: #4b5563;
}

.true-false-content:hover {
  border-color: #d1d5db;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.06);
  transform: translateY(-1px);
}

.dark .true-false-content:hover {
  border-color: #6b7280;
}

.true-false-icon {
  width: 1.5rem;
  height: 1.5rem;
  border: 2px solid #d1d5db;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  background-color: #f9fafb;
}

.dark .true-false-icon {
  border-color: #4b5563;
  background-color: #374151;
}

.true-false-option.selected .true-false-icon {
  border-color: #3b82f6;
  background-color: #3b82f6;
  color: white;
}

.dark .true-false-option.selected .true-false-icon {
  border-color: #60a5fa;
  background-color: #60a5fa;
}

.true-false-text {
  font-size: 1rem;
  font-weight: 500;
  color: #374151;
}

.dark .true-false-text {
  color: #d1d5db;
}

.true-false-option.selected .true-false-text {
  color: #1e40af;
  font-weight: 600;
}

.dark .true-false-option.selected .true-false-text {
  color: #93c5fd;
}

/* Enhanced focus states for accessibility */
.radio-input:focus-visible + .radio-button,
.radio-input:focus-visible ~ .true-false-content .radio-button {
  outline: 2px solid #60a5fa;
  outline-offset: 2px;
}
</style>

<style scoped>
/* Make radio option layout wrap on small screens */
.radio-option-content {
  display: flex;
  align-items: center;
  flex: 1;
  gap: 0.75rem;
  flex-wrap: wrap;
}

/* Ensure the text input can shrink and grow properly inside flex */
.radio-option-content input[type="text"],
.radio-option-content textarea {
  min-width: 0;
  flex: 1 1 auto;
}

/* Tighter paddings and full-width input on small screens */
@media (max-width: 640px) {
  .radio-option-wrapper {
    padding: 0.5rem;
  }

  .radio-button-wrapper {
    margin-right: 0.25rem;
  }

  .option-letter {
    min-width: auto;
    font-size: 0.8rem;
  }

  /* Make the text field take the full row under the radio */
  .radio-option-content input[type="text"] {
    flex-basis: 100%;
    width: 100%;
  }

  /* Place remove button on the right by itself */
  .radio-option-content > button {
    flex-shrink: 0;
  }
}

/* Remove button variants */
.remove-text-btn {
  background: transparent;
  border: 1px solid transparent;
}
.remove-text-btn:hover {
  background: rgba(239, 68, 68, 0.06);
}
.remove-icon-btn {
  background: transparent;
  border: none;
}
</style>
